import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import {
  ShieldCheck, Search, Award, Download, MessageCircle, Ban, CheckCircle2, XCircle, FileCheck2, User, Loader2, Mail, Eye,
  FileSpreadsheet, FileText, FileJson,
} from "lucide-react";
import { useContribuyentesSearch } from "../hooks/useContribuyentesSearch";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPalmaLogo, drawPalmaFooter, fechaHoraVE } from "../lib/pdfFooter";

const estatusBadge = (v) => v === "Vigente"
  ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Vigente</Badge>
  : v === "Vencida"
  ? <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Vencida</Badge>
  : v === "Anulada"
  ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 line-through">Anulada</Badge>
  : <Badge className="bg-emerald-100 text-emerald-800">{v || "-"}</Badge>;

// ============================== Exportar solvencias filtradas ==============================
function solvToRow(s) {
  return {
    "Nº Solvencia": s.nro_solvencia || "",
    "RIF / Cédula": s.contribuyente_rif || "",
    "Contribuyente": s.contribuyente_nombre || "",
    "Período Cubierto": s.periodo_cubierto || "",
    "Fecha Emisión": s.fecha_emision || "",
    "Fecha Vencimiento": s.fecha_vencimiento || "",
    "Estatus": s.estatus || "",
    "Motivo Anulación": s.motivo_anulacion || "",
    "Emitida por": s.emitida_por_nombre || "",
  };
}

function exportarSolvencias({ formato, rows }) {
  if (!rows.length) { toast.error("Nada que exportar con los filtros actuales"); return; }
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `solvencias_historial_${stamp}`;

  if (formato === "excel") {
    const data = rows.map(solvToRow);
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Solvencias");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Excel descargado (${rows.length} solvencias)`);
  } else if (formato === "json") {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.json`; a.click(); URL.revokeObjectURL(url);
    toast.success(`JSON descargado (${rows.length} solvencias)`);
  } else if (formato === "pdf") {
    loadPalmaLogo().then(logoDataURL => {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45); doc.text("SISAUR · Solvencias emitidas", 40, 40);
      doc.setFontSize(11); doc.setTextColor(80);
      doc.text(`Reporte: Historial de Solvencias`, 40, 58);
      doc.text(`Generado: ${fecha} ${hora} · Total: ${rows.length} solvencias`, 40, 74);

      const head = [["Nº Solvencia", "RIF", "Contribuyente", "Período", "Emisión", "Vencimiento", "Estatus"]];
      const body = rows.map(s => [
        s.nro_solvencia || "—",
        s.contribuyente_rif || "—",
        (s.contribuyente_nombre || "—").slice(0, 45),
        s.periodo_cubierto || "—",
        s.fecha_emision || "—",
        s.fecha_vencimiento || "—",
        s.estatus || "—",
      ]);
      autoTable(doc, {
        head, body, startY: 94,
        margin: { bottom: 60 },
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [20, 83, 45], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        didDrawPage: (data) => drawPalmaFooter(doc, { logoDataURL, fecha, hora, pageNumber: data.pageNumber }),
      });
      doc.save(`${filename}.pdf`);
      toast.success(`PDF descargado (${rows.length} solvencias)`);
    });
  }
}

export default function SolvenciasPage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState("emitir");

  // === Emitir ===
  // Search server-side (debounce + merge) — reemplaza el filtro local que limitaba
  // a los primeros 200 contribuyentes y no encontraba a los demás.
  const {
    contribuyentes,
    contribSearch: search,
    setContribSearch: setSearch,
    loading: searching,
  } = useContribuyentesSearch({ initialLimit: 50, searchLimit: 100, minChars: 2, debounceMs: 250 });

  const [selected, setSelected] = useState(null);
  const [eleg, setEleg] = useState(null);
  const [loadingEleg, setLoadingEleg] = useState(false);
  const [emitiendo, setEmitiendo] = useState(false);

  // === Historial ===
  const [solvencias, setSolvencias] = useState([]);
  const emptyHistFilters = { nro: "", rif: "", contribuyente: "", periodo: "", fecha_emision: "", fecha_vencimiento: "", estatus: "todos" };
  const [histFilters, setHistFilters] = useState(emptyHistFilters);

  const loadHistorial = () => axios.get(`${API}/solvencias`, auth).then(r => setSolvencias(r.data)).catch(() => {});
  useEffect(() => { if (tab === "historial") loadHistorial(); }, [tab]); // eslint-disable-line

  // Cómputo de estatus efectivo (Vigente/Vencida/Anulada) tomando en cuenta anulaciones
  const solvEstatus = (s) => (s.estatus === "Anulada" ? "Anulada" : (s.estatus || "-"));

  const solvenciasFiltradas = useMemo(() => {
    const nro = histFilters.nro.trim().toLowerCase();
    const rif = histFilters.rif.trim().toLowerCase();
    const cn = histFilters.contribuyente.trim().toLowerCase();
    const per = histFilters.periodo.trim().toLowerCase();
    return solvencias.filter(s => {
      if (nro && !(s.nro_solvencia || "").toLowerCase().includes(nro)) return false;
      if (rif && !(s.contribuyente_rif || "").toLowerCase().includes(rif)) return false;
      if (cn && !(s.contribuyente_nombre || "").toLowerCase().includes(cn)) return false;
      if (per && !(s.periodo_cubierto || "").toLowerCase().includes(per)) return false;
      if (histFilters.fecha_emision && (s.fecha_emision || "") !== histFilters.fecha_emision) return false;
      if (histFilters.fecha_vencimiento && (s.fecha_vencimiento || "") !== histFilters.fecha_vencimiento) return false;
      if (histFilters.estatus !== "todos" && solvEstatus(s) !== histFilters.estatus) return false;
      return true;
    });
  }, [solvencias, histFilters]);

  const clearHistFilters = () => setHistFilters(emptyHistFilters);
  const histActive = !!(histFilters.nro || histFilters.rif || histFilters.contribuyente || histFilters.periodo || histFilters.fecha_emision || histFilters.fecha_vencimiento || histFilters.estatus !== "todos");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contribuyentes.slice(0, 25);
    return contribuyentes.filter(c =>
      (c.nombre || "").toLowerCase().includes(q) ||
      (c.documento || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.razon_social || "").toLowerCase().includes(q) ||
      (c.denominacion_comercial || "").toLowerCase().includes(q)
    ).slice(0, 25);
  }, [search, contribuyentes]);

  const seleccionar = async (c) => {
    setSelected(c);
    setEleg(null);
    setLoadingEleg(true);
    try {
      const r = await axios.get(`${API}/solvencias/elegibilidad/${c.id}`, auth);
      setEleg(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al verificar elegibilidad");
    } finally { setLoadingEleg(false); }
  };

  const emitir = async () => {
    if (!selected) return;
    setEmitiendo(true);
    try {
      const r = await axios.post(`${API}/solvencias/emitir`, { contribuyente_id: selected.id }, auth);
      toast.success(r.data?.created ? "Solvencia emitida" : "Solvencia ya existente para el mes en curso");
      const sol = r.data?.solvencia;
      if (sol) await descargarPDF(sol.id, sol.nro_solvencia);
      loadHistorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo emitir la solvencia");
    } finally { setEmitiendo(false); }
  };

  const descargarPDF = async (id, nro) => {
    const r = await axios.get(`${API}/solvencias/${id}/pdf`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `solvencia_${(nro || "").replace(/\//g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const verPDF = async (id) => {
    try {
      const r = await axios.get(`${API}/solvencias/${id}/pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      // Abre en nueva pestaña para previsualización sin descargar
      const w = window.open(url, "_blank");
      if (!w) {
        toast.error("Su navegador bloqueó la ventana emergente. Permita pop-ups para previsualizar.");
      }
      // Libera el objectURL luego de un rato (el navegador ya lo mantiene abierto)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo abrir el PDF");
    }
  };

  const anular = async (s) => {
    if (!window.confirm(`¿Anular la solvencia ${s.nro_solvencia}? Esta acción no se puede revertir.`)) return;
    const motivo = window.prompt("Motivo de anulación (opcional):", "") || "";
    try {
      await axios.post(`${API}/solvencias/${s.id}/anular`, { motivo }, auth);
      toast.success("Solvencia anulada");
      loadHistorial();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al anular"); }
  };

  const enviarWhatsApp = async (s) => {
    try {
      const contrib = contribuyentes.find(c => c.id === s.contribuyente_id) || {};
      const raw = (contrib.celular || contrib.telefono || "").replace(/\D/g, "");
      if (!raw) { toast.error("El contribuyente no tiene celular registrado"); return; }
      const phone = raw.startsWith("58") ? raw : (raw.startsWith("0") ? "58" + raw.slice(1) : "58" + raw);
      const st = await axios.get(`${API}/solvencias/${s.id}/share-token`, auth);
      const shareToken = st.data?.share_token;
      const pdfUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/public/solvencia/${s.id}/pdf?token=${shareToken}`;
      const nombre = contrib.nombre || "Contribuyente";
      const msg =
        `¡Hola ${nombre}! 🌿\n\n` +
        `Palma Plus, Saneamiento Urbano le entrega su *Certificado de Solvencia* del Impuesto de Aseo Urbano correspondiente al período vigente. ✅\n\n` +
        `📄 Descárguelo aquí:\n${pdfUrl}\n\n` +
        `Válida hasta: ${s.fecha_vencimiento}\n` +
        `N° Solvencia: ${s.nro_solvencia}\n\n` +
        `Gracias por mantener su comunidad al día. Por una ciudad más limpia. 🌱\n— Palma Plus, Saneamiento Urbano`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar por WhatsApp");
    }
  };

  const enviarEmail = async (s) => {
    try {
      toast.loading(`Enviando solvencia ${s.nro_solvencia} por email...`, { id: `sol-email-${s.id}` });
      const r = await axios.post(`${API}/solvencias/${s.id}/enviar-email`, {}, auth);
      const status = r.data?.email_status;
      if (status === "enviado") {
        toast.success(`Email enviado a ${r.data?.destino || "el contribuyente"}`, { id: `sol-email-${s.id}` });
      } else if (status === "sin_email") {
        toast.error("El contribuyente no tiene email registrado", { id: `sol-email-${s.id}` });
      } else if (status === "sin_api_key") {
        toast.error("Servicio de email no configurado", { id: `sol-email-${s.id}` });
      } else if (status === "error") {
        toast.error(`Error: ${r.data?.email_error || "no se pudo enviar"}`, { id: `sol-email-${s.id}` });
      } else {
        toast.success(`Envío procesado (${status})`, { id: `sol-email-${s.id}` });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el email", { id: `sol-email-${s.id}` });
    }
  };

  return (
    <div className="space-y-6" data-testid="solvencias-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-orange-500" /> Solvencias
        </h1>
        <p className="text-green-700/70 mt-1">
          Emita certificados de solvencia de aseo urbano para contribuyentes con pago verificado del mes en curso.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="emitir" data-testid="tab-solv-emitir">Emitir solvencia</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-solv-historial">Historial</TabsTrigger>
        </TabsList>

        {/* ============ EMITIR ============ */}
        <TabsContent value="emitir" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Buscador */}
            <Card className="p-4 border-green-100">
              <h3 className="font-display font-bold text-lg text-green-900 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" /> 1. Seleccione un contribuyente
              </h3>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, RIF/documento o email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-buscar-contribuyente"
                />
                {searching && (
                  <Loader2 className="w-4 h-4 absolute right-3 top-3 text-orange-500 animate-spin" data-testid="solv-search-loading" />
                )}
              </div>
              {search.trim().length > 0 && search.trim().length < 2 && (
                <p className="text-xs text-slate-500 mb-2 px-1">
                  Escriba al menos 2 caracteres para buscar en toda la base.
                </p>
              )}
              <div className="max-h-[420px] overflow-y-auto border border-green-100 rounded-lg divide-y divide-green-50">
                {filtered.length === 0 && (
                  <p className="p-6 text-center text-slate-400 text-sm">
                    {searching ? "Buscando..." : "Sin resultados"}
                  </p>
                )}
                {filtered.map(c => (
                  <button
                    key={c.id}
                    onClick={() => seleccionar(c)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-orange-50 transition text-sm ${selected?.id === c.id ? "bg-orange-50/70 border-l-4 border-l-orange-500" : ""}`}
                    data-testid={`contrib-item-${c.documento}`}
                  >
                    <p className="font-semibold text-green-900">{c.nombre}</p>
                    <p className="text-xs text-slate-500">
                      {c.tipo_documento}-{c.documento} · {c.email || "sin email"}
                    </p>
                  </button>
                ))}
              </div>
            </Card>

            {/* Elegibilidad */}
            <Card className="p-4 border-green-100">
              <h3 className="font-display font-bold text-lg text-green-900 mb-3 flex items-center gap-2">
                <FileCheck2 className="w-5 h-5 text-orange-500" /> 2. Verificación y emisión
              </h3>

              {!selected && (
                <div className="text-center py-10 text-slate-400 text-sm">
                  Seleccione un contribuyente para verificar su elegibilidad.
                </div>
              )}

              {selected && loadingEleg && (
                <div className="text-center py-10 text-green-700 text-sm">Verificando...</div>
              )}

              {selected && eleg && !loadingEleg && (
                <div className="space-y-4">
                  <div className="bg-green-50/60 rounded-lg p-3">
                    <p className="text-xs text-green-800 uppercase font-bold tracking-wide">Contribuyente</p>
                    <p className="text-green-900 font-semibold">{selected.nombre}</p>
                    <p className="text-xs text-slate-500">{selected.tipo_documento}-{selected.documento}</p>
                  </div>

                  <div className={`rounded-lg p-4 border ${eleg.elegible ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                    <div className="flex items-center gap-2">
                      {eleg.elegible ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <XCircle className="w-5 h-5 text-rose-700" />}
                      <p className={`font-bold ${eleg.elegible ? "text-emerald-900" : "text-rose-900"}`}>
                        {eleg.elegible ? "Elegible para solvencia" : "No elegible"}
                      </p>
                    </div>
                    <p className={`text-sm mt-1 ${eleg.elegible ? "text-emerald-800" : "text-rose-800"}`}>
                      {eleg.elegible
                        ? `Tiene pago validado que cubre ${eleg.periodo_actual_label}.`
                        : eleg.razon}
                    </p>
                  </div>

                  {eleg.pagos_recientes?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
                        Últimos pagos validados
                      </p>
                      <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
                        {eleg.pagos_recientes.slice(0, 5).map((p, i) => (
                          <div key={i} className="flex justify-between gap-2 border-b border-slate-100 py-1">
                            <span className="font-mono">{p.nro_comprobante || "-"}</span>
                            <span>{p.fecha_pago}</span>
                            <span className="font-semibold">Bs. {(Number(p.monto_pagado) || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={emitir}
                    disabled={!eleg.elegible || emitiendo}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    data-testid="btn-emitir-solvencia"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    {emitiendo ? "Emitiendo..." : "Emitir y descargar Solvencia"}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ============ HISTORIAL ============ */}
        <TabsContent value="historial" className="mt-4">
          <Card className="p-4 border-green-100">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="font-display font-bold text-lg text-green-900">Solvencias emitidas</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500" data-testid="solv-hist-summary">
                  Mostrando <b className="text-green-800">{solvenciasFiltradas.length}</b> de <b>{solvencias.length}</b>
                  {histActive ? " · filtros activos" : ""}
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!solvenciasFiltradas.length}
                      data-testid="solv-btn-exportar"
                      className="h-8 border-green-200 text-green-800 hover:bg-green-50"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Exportar
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-52 p-1">
                    <button
                      data-testid="solv-export-excel"
                      onClick={() => exportarSolvencias({ formato: "excel", rows: solvenciasFiltradas })}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel (.xlsx)
                    </button>
                    <button
                      data-testid="solv-export-pdf"
                      onClick={() => exportarSolvencias({ formato: "pdf", rows: solvenciasFiltradas })}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left"
                    >
                      <FileText className="w-4 h-4 text-orange-600" /> PDF (.pdf)
                    </button>
                    <button
                      data-testid="solv-export-json"
                      onClick={() => exportarSolvencias({ formato: "json", rows: solvenciasFiltradas })}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left"
                    >
                      <FileJson className="w-4 h-4 text-slate-600" /> JSON (.json)
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Barra de filtros multi-campo */}
            <div className="mb-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="solv-hist-filtros">
              <Input
                data-testid="solv-filtro-nro"
                value={histFilters.nro}
                onChange={e => setHistFilters(f => ({ ...f, nro: e.target.value }))}
                placeholder="Nº Solvencia"
                className="bg-green-50/60 border-green-100 h-9"
              />
              <Input
                data-testid="solv-filtro-rif"
                value={histFilters.rif}
                onChange={e => setHistFilters(f => ({ ...f, rif: e.target.value }))}
                placeholder="RIF / Cédula"
                className="bg-green-50/60 border-green-100 h-9"
              />
              <Input
                data-testid="solv-filtro-contribuyente"
                value={histFilters.contribuyente}
                onChange={e => setHistFilters(f => ({ ...f, contribuyente: e.target.value }))}
                placeholder="Contribuyente"
                className="bg-green-50/60 border-green-100 h-9"
              />
              <Input
                data-testid="solv-filtro-periodo"
                value={histFilters.periodo}
                onChange={e => setHistFilters(f => ({ ...f, periodo: e.target.value }))}
                placeholder="Período (ej. Enero 2026)"
                className="bg-green-50/60 border-green-100 h-9"
              />

              {/* Filtros por FECHA de EMISIÓN y FECHA de VENCIMIENTO (independientes, no rango) */}
              <div className="col-span-2 md:col-span-1">
                <p className="text-[10px] font-semibold text-green-800 uppercase tracking-wide mb-0.5">Fecha de Emisión</p>
                <Input
                  data-testid="solv-filtro-fecha-emision"
                  type="date"
                  value={histFilters.fecha_emision}
                  onChange={e => setHistFilters(f => ({ ...f, fecha_emision: e.target.value }))}
                  className="bg-green-50/60 border-green-100 h-9"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <p className="text-[10px] font-semibold text-green-800 uppercase tracking-wide mb-0.5">Fecha de Vencimiento</p>
                <Input
                  data-testid="solv-filtro-fecha-vencimiento"
                  type="date"
                  value={histFilters.fecha_vencimiento}
                  onChange={e => setHistFilters(f => ({ ...f, fecha_vencimiento: e.target.value }))}
                  className="bg-green-50/60 border-green-100 h-9"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <p className="text-[10px] font-semibold text-green-800 uppercase tracking-wide mb-0.5">Estatus</p>
                <Select value={histFilters.estatus} onValueChange={v => setHistFilters(f => ({ ...f, estatus: v }))}>
                  <SelectTrigger data-testid="solv-filtro-estatus" className="bg-green-50/60 border-green-100 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los estatus</SelectItem>
                    <SelectItem value="Vigente">Vigente</SelectItem>
                    <SelectItem value="Vencida">Vencida</SelectItem>
                    <SelectItem value="Anulada">Anulada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {histActive && (
                <div className="col-span-2 md:col-span-1 flex items-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearHistFilters}
                    data-testid="solv-filtro-clear"
                    className="h-9 w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    ✕ Limpiar filtros
                  </Button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900">
                  <tr>
                    <th className="text-left p-2.5">N° Solvencia</th>
                    <th className="text-left p-2.5">RIF</th>
                    <th className="text-left p-2.5">Contribuyente</th>
                    <th className="text-left p-2.5">Período</th>
                    <th className="text-left p-2.5">Emisión</th>
                    <th className="text-left p-2.5">Vencimiento</th>
                    <th className="text-center p-2.5">Estatus</th>
                    <th className="text-right p-2.5">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {solvenciasFiltradas.map(s => (
                    <tr key={s.id} className="border-b border-green-50" data-testid={`solv-row-${s.nro_solvencia}`}>
                      <td className="p-2.5 font-mono">{s.nro_solvencia}</td>
                      <td className="p-2.5 font-mono text-xs">{s.contribuyente_rif || "-"}</td>
                      <td className="p-2.5">{s.contribuyente_nombre}</td>
                      <td className="p-2.5">{s.periodo_cubierto}</td>
                      <td className="p-2.5">{s.fecha_emision}</td>
                      <td className="p-2.5">{s.fecha_vencimiento}</td>
                      <td className="p-2.5 text-center">{estatusBadge(s.estatus)}</td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => verPDF(s.id)} title="Ver PDF (previsualizar)" data-testid={`solv-ver-${s.nro_solvencia}`}>
                          <Eye className="w-4 h-4 text-slate-700" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => descargarPDF(s.id, s.nro_solvencia)} title="Descargar PDF" data-testid={`solv-download-${s.nro_solvencia}`}>
                          <Download className="w-4 h-4 text-green-700" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => enviarEmail(s)} title="Enviar por email" data-testid={`solv-email-${s.nro_solvencia}`}>
                          <Mail className="w-4 h-4 text-orange-600" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => enviarWhatsApp(s)} title="Enviar por WhatsApp" data-testid={`solv-wa-${s.nro_solvencia}`}>
                          <MessageCircle className="w-4 h-4 text-emerald-600" />
                        </Button>
                        {s.estatus === "Vigente" && (
                          <Button size="icon" variant="ghost" onClick={() => anular(s)} title="Anular" data-testid={`solv-anular-${s.nro_solvencia}`}>
                            <Ban className="w-4 h-4 text-orange-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!solvenciasFiltradas.length && (
                    <tr><td colSpan="8" className="p-8 text-center text-slate-400">
                      {solvencias.length ? "Ninguna solvencia coincide con los filtros" : "Sin solvencias emitidas"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
